import { pdfjs } from "react-pdf";

// IMPORTANT: import pdfjs from react-pdf, NOT from pdfjs-dist directly.
// Two copies of pdf.js with mismatched workers => silent "fake worker"
// fallback => main-thread parsing => page freeze.
//
// Somewhere at app entry, workerSrc must be set. Match the extension to
// your pdfjs-dist major version (v4 = .mjs, v3 = .js):
//
//   pdfjs.GlobalWorkerOptions.workerSrc = new URL(
//     "pdfjs-dist/build/pdf.worker.min.mjs",
//     import.meta.url
//   ).toString();

const MAX_THUMB_BYTES = 20 * 1024 * 1024; // must match MAX_THUMB_MB in the card

const cache = new Map(); // docId -> blobUrl
const failed = new Set(); // docId -> permanently failed this session
const inflight = new Map(); // docId -> Promise<blobUrl>
let queue = Promise.resolve(); // one heavy PDF parse at a time

export function getPdfThumbnail(docId, url, width, height) {
  if (cache.has(docId)) return Promise.resolve(cache.get(docId));
  if (failed.has(docId)) return Promise.reject(new Error("previously failed"));
  if (inflight.has(docId)) return inflight.get(docId);

  const p = (queue = queue
    .catch(() => {}) // a failed doc must not poison the queue
    .then(() => renderThumb(docId, url, width, height)))
    .then((blobUrl) => {
      cache.set(docId, blobUrl);
      inflight.delete(docId);
      return blobUrl;
    })
    .catch((e) => {
      failed.add(docId); // never re-attempt (and re-download) this session
      inflight.delete(docId);
      throw e;
    });

  inflight.set(docId, p);
  return p;
}

// Defense in depth: even if the card's file_size parse is wrong or missing,
// refuse to pull a huge file. Costs one HEAD request.
async function assertSizeOk(url) {
  try {
    const res = await fetch(url, { method: "HEAD" });
    const len = Number(res.headers.get("content-length"));
    if (len && len > MAX_THUMB_BYTES) {
      throw new Error(`file too large for client thumbnail: ${len} bytes`);
    }
  } catch (e) {
    if (String(e?.message).includes("too large")) throw e;
    // HEAD blocked by CORS/405 -> can't verify, proceed; the card's
    // file_size gate is the primary guard.
  }
}

async function renderThumb(docId, url, width, height) {
  if (cache.has(docId)) return cache.get(docId);

  await assertSizeOk(url);

  const loadingTask = pdfjs.getDocument({
    url,
    disableAutoFetch: true,
    disableStream: true,
  });

  try {
    const pdf = await loadingTask.promise;
    const page = await pdf.getPage(1);

    const base = page.getViewport({ scale: 1 });
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const scale = Math.min(width / base.width, height / base.height) * dpr;
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    await page.render({
      canvasContext: canvas.getContext("2d"),
      viewport,
    }).promise;

    const blob = await new Promise((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/jpeg", 0.8)
    );
    return URL.createObjectURL(blob);
  } finally {
    // Worker transport destroyed in a detached function =>
    // component unmounts can never race it.
    loadingTask.destroy();
  }
}
